import { getSupabase } from "@/lib/supabase/client";
import { useCallback, useEffect, useState } from "react";


export type StepProgress = {
  status?: "not_started" | "in_progress" | "done" | "stuck";
  due_date?: string | null;
  owner?: string | null;
  comment?: string | null;
};

export function useLinkedRoadmap(projectId: string | null) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState({
    chapters: [] as any[],
    stepsByChapter: {} as Record<string, any[]>,
    progressByStep: {} as Record<string, StepProgress>,
  });

  const loadData = useCallback(async () => {
    if (!projectId) return;
    try {
      setLoading(true);
      const sb = getSupabase();

      const [blueprintRes, progressRes] = await Promise.all([
        sb
          .from("roadmap_projects")
          .select(
            `
            id,
            chapters:roadmap_chapters(
              *,
              steps:roadmap_steps(*)
            )
          `,
          )
          .eq("is_published", true)
          .order("version", { ascending: false })
          .limit(1)
          .maybeSingle(),
        sb.from("roadmap_progress").select("*").eq("project_id", projectId),
      ]);

      if (blueprintRes.error) throw blueprintRes.error;
      const blueprint = blueprintRes.data;

      if (!blueprint) {
        setLoading(false);
        return;
      }

      const sortedChapters = (blueprint.chapters || []).sort(
        (a: any, b: any) => (a.order ?? 0) - (b.order ?? 0),
      );

      const stepsMap: Record<string, any[]> = {};
      sortedChapters.forEach((ch: any) => {
        stepsMap[ch.id] = (ch.steps || []).sort(
          (a: any, b: any) => (a.order ?? 0) - (b.order ?? 0),
        );
      });

      const progressMap: Record<string, StepProgress> = {};
      (progressRes.data || []).forEach((p: any) => {
        progressMap[String(p.step_id)] = {
          status: p.status,
          due_date: p.due_date,
          owner: p.owner,
          comment: p.comment,
        };
      });

      setData({
        chapters: sortedChapters,
        stepsByChapter: stepsMap,
        progressByStep: progressMap,
      });
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const upsertProgress = async (stepId: string, patch: StepProgress) => {
    if (!projectId) return;
    const sb = getSupabase();

    setData((prev) => ({
      ...prev,
      progressByStep: {
        ...prev.progressByStep,
        [stepId]: { ...prev.progressByStep[stepId], ...patch },
      },
    }));

    const { error } = await sb
      .from("roadmap_progress")
      .upsert(
        { project_id: projectId, step_id: stepId, ...patch },
        { onConflict: "project_id,step_id" },
      );

    if (error) throw error;
  };

  return {
    ...data,
    loading,
    error,
    setStatus: (id: string, s: any) => upsertProgress(id, { status: s }),
    setDueDate: (id: string, d: any) => upsertProgress(id, { due_date: d }),
    setOwner: (id: string, o: any) => upsertProgress(id, { owner: o }),
    setComment: (id: string, c: any) => upsertProgress(id, { comment: c }),
  };
}
